import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  formatQuantity,
  isSourcingFinished,
  nextSourcingStatuses,
  type SourcingRequirement,
} from '@tailoring-crm/shared';
import { formatCurrency } from '../../lib/format.js';
import {
  useDeleteSourcingRequirement,
  useSetSourcingStatus,
} from '../../lib/sourcing/use-sourcing.js';
import {
  Button,
  useConfirm,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useToast,
} from '@tailoring-crm/ui';
import { SourcingStatusBadge } from './sourcing-status-badge.js';

/**
 * One requirement, as both screens that list them draw it.
 *
 * WHICH STEPS ARE OFFERED COMES FROM THE SHARED TABLE, never from this component's own idea of the
 * lifecycle. `nextSourcingStatuses` is the same function the API refuses a 409 against, so a shop
 * is never shown a button that the server will decline — which, on a screen somebody uses standing
 * at a counter, is the difference between a tool and an argument.
 *
 * A FINISHED ROW OFFERS NOTHING. Received or cancelled, it is a record of what happened: no
 * advance, no edit, no delete. Deleting is narrower still — the API allows it only while nobody
 * has started, so the button disappears the moment somebody has.
 *
 * The row is deliberately identical on the order's card and on the sourcing list. The only thing
 * that differs is whose order it is: on an order's own card the customer and the number are
 * already at the top of the page, so `showOrder` leaves them off rather than printing them twice.
 */
export interface SourcingRowProps {
  requirement: SourcingRequirement;
  /** Show the customer and the order number. Off on the order's own page, where they repeat. */
  showOrder?: boolean;
  onEdit: (requirement: SourcingRequirement) => void;
}

export function SourcingRow({
  requirement,
  showOrder = false,
  onEdit,
}: SourcingRowProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const advance = useSetSourcingStatus();
  const remove = useDeleteSourcingRequirement();

  const finished = isSourcingFinished(requirement.status);
  const steps = nextSourcingStatuses(requirement.status);
  const busy = advance.isPending || remove.isPending;

  /** A refusal is the shop's to hear about, not the console's. */
  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const onAdvance = async (status: (typeof steps)[number]): Promise<void> => {
    try {
      await advance.mutateAsync({ sourcingId: requirement.id, input: { status } });
    } catch (error) {
      report(error);
    }
  };

  const onDelete = async (): Promise<void> => {
    const ok = await confirm({
      title: t('sourcing.delete.title'),
      description: t('sourcing.delete.description'),
      confirmLabel: t('common.delete'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync({ sourcingId: requirement.id, orderId: requirement.orderId });
    } catch (error) {
      report(error);
    }
  };

  return (
    <li
      data-testid="sourcing-row"
      className="flex flex-wrap items-start justify-between gap-2 rounded-md border border-border p-3 text-sm"
    >
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          <span>{requirement.productLabel || t('sourcing.wholeOrder')}</span>
          <span className="text-muted-foreground">{t(`sourcing.type.${requirement.type}`)}</span>
          <SourcingStatusBadge status={requirement.status} />
        </p>

        {showOrder ? (
          <p>
            {/* Straight to the order, which is where the garment, the dates and the money are. */}
            <Link to={`/orders/${requirement.orderId}`} className="font-medium hover:underline">
              {requirement.customerName}
            </Link>
            <span className="text-muted-foreground"> · {requirement.orderNumber}</span>
          </p>
        ) : null}

        {/* What actually has to be bought. When a garment is linked the line above names the
            garment, and "Blouse · Fabrics · 6 m" does not tell anybody what to ask the shop for. */}
        {requirement.description && requirement.description !== requirement.productLabel ? (
          <p className="text-xs text-muted-foreground">{requirement.description}</p>
        ) : null}

        <p className="text-xs text-muted-foreground">
          {t('sourcing.quantityWithAmount', {
            quantity: `${formatQuantity(requirement.quantity, requirement.unit)} ${t(`sourcing.unit.${requirement.unit}`)}`,
            amount: formatCurrency(requirement.amountPaise),
          })}
        </p>

        {requirement.notes ? (
          <p className="text-xs text-muted-foreground">{requirement.notes}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1">
        {/* Only what the API will accept. A finished row gets no menu at all. */}
        {steps.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={busy}>
                {t('sourcing.advance')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {steps.map((status) => (
                <DropdownMenuItem key={status} onSelect={() => void onAdvance(status)}>
                  {t(`sourcing.moveTo.${status}`)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {!finished ? (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => onEdit(requirement)}>
            {t('common.edit')}
          </Button>
        ) : null}

        {/* Narrower than editing: once anybody has started, the honest ending is cancelled. */}
        {requirement.status === 'to_start' ? (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void onDelete()}>
            {t('common.delete')}
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export default SourcingRow;
